import { type ColDef } from 'ag-grid-community';

import { EmployeeCell } from './components/EmployeeCell';
import { ParticipationPercentage } from './components/ParticipationPercentage';
import { PlainTextCell } from './components/PlainTextCell';
import TopicDistributionSplitBar from './components/TopicDistributionSplitBar';

interface OneOnOneRabbitInsightsColumnDefsInterface {
  [key: string]: (params: { displayName: string; id: string }) => ColDef;
}

export const oneOnOneRabbitInsightsColumnDefs: OneOnOneRabbitInsightsColumnDefsInterface = {
  EMPLOYEE_NAME: ({ displayName, id }) => ({
    colId: id,
    field: id,
    flex: 1,
    headerName: displayName,
    minWidth: 300,
    filter: true,
    pinned: 'left',
    filterValueGetter: ({ data }) => data && data[1]?.value?.displayName,
    valueGetter: ({ data }) => data && data[id]?.value,
    cellRenderer: EmployeeCell,
    sortable: true,
    comparator: (value1, value2) =>
      String(value1?.displayName).toLocaleLowerCase().localeCompare(String(value2?.displayName).toLocaleLowerCase()),
    // REFER: https://stackoverflow.com/a/68768062/8487836
    hide: id === '7' || id === '8' || id === '10',
    suppressColumnsToolPanel: id === '1' || id === '8' || id === '10' || id === '7',
    getQuickFilterText: ({ data }) => data && data[1]?.value?.displayName,
  }),
  HIERARCHY: ({ displayName, id }) => ({
    colId: id,
    field: id,
    headerName: displayName,
    valueGetter: ({ data }) => data && data.hierarchy.value?.join('&'),
    hide: id === '-2',
    suppressColumnsToolPanel: id === '-2',
  }),
  MANAGER_EMAIL: ({ displayName, id }) => ({
    colId: id,
    field: id,
    headerName: displayName,
    valueGetter: ({ data }) => data && data[1]?.value?.participantEmail,
    hide: id === '-3',
    suppressColumnsToolPanel: id === '-3',
  }),
  EMPLOYEE_EMAIL: ({ displayName, id }) => ({
    colId: id,
    field: id,
    headerName: displayName,
    valueGetter: ({ data }) => data && data['1'].value?.email,
    hide: id === '-4',
    suppressColumnsToolPanel: id === '-4',
  }),
  ONE_ON_ONE_AVERAGE_GAP: ({ displayName, id }) => ({
    colId: id,
    field: id,
    minWidth: 200,
    headerName: displayName,
    filter: 'agNumberColumnFilter',
    filterValueGetter: ({ data }) => data && (Number(data[id]?.value) ?? 0),
    valueGetter: ({ data }) =>
      data &&
      (data[id]?.value !== null
        ? `${Math.round(data[id]?.value)} ${Number(data[id]?.value) > 0 ? 'days' : 'day'}`
        : null),
    cellRenderer: PlainTextCell,
    sortable: true,
  }),
  ONE_ON_ONE_COMPLETION_PERCENTAGE: ({ id }) => ({
    colId: id,
    field: id,
    minWidth: 200,
    headerName: 'Completion',
    filter: 'agNumberColumnFilter',
    filterValueGetter: ({ data }) => data && data[id]?.value,
    valueGetter: ({ data }) => data && (typeof data[id]?.value === 'number' ? `${data[id]?.value ?? 0}%` : null),
    cellRenderer: PlainTextCell,
    sortable: true,
  }),
  ONE_ON_ONE_SATISFACTION_SCORE: ({ displayName, id }) => ({
    colId: id,
    field: id,
    minWidth: 200,
    headerName: displayName,
    filter: 'agNumberColumnFilter',
    filterValueGetter: ({ data }) => data && (Number(data[id]?.value) ?? 'NA'),
    valueGetter: ({ data }) => data && (typeof data[id]?.value === 'number' ? `${data[id]?.value ?? 0}%` : null),
    cellRenderer: PlainTextCell,
    sortable: true,
  }),
  ONE_ON_ONE_ADMIN_AGENDA_PERCENTAGE: ({ displayName, id }) => ({
    colId: id,
    field: id,
    minWidth: 200,
    headerName: displayName,
    wrapHeaderText: true, // <- this breaks the header text to multiple lines
    autoHeaderHeight: true,
    filter: 'agNumberColumnFilter',
    filterValueGetter: ({ data }) => data && (Number(data[id]?.value) ?? 'NA'),
    valueGetter: ({ data }) => data && (typeof data[id]?.value === 'number' ? `${data[id]?.value ?? 0}%` : null),
    cellRenderer: PlainTextCell,
    sortable: true,
    toolPanelClass: 'ag-column-tool-panel-one-on-one-admin-agenda-percentage',
  }),
  ONE_ON_ONE_THEME_DISTRIBUTION: ({ displayName, id }) => ({
    colId: id,
    field: id,
    minWidth: 250,
    headerName: displayName,
    autoHeaderHeight: true,
    filter: 'agNumberColumnFilter',
    filterValueGetter: ({ data }) => data[id]?.value,
    valueGetter: ({ data }) => data[id]?.value ?? {},
    cellRenderer: TopicDistributionSplitBar,
    suppressHeaderMenuButton: true,
    sortable: false,
    toolPanelClass: 'ag-column-tool-panel-one-on-one-admin-agenda-percentage',
  }),
  ONE_ON_ONE_PARTICIPATION_PERCENTAGE: ({ displayName, id }) => ({
    colId: id,
    field: id,
    minWidth: 200,
    headerName: displayName,
    filter: 'agNumberColumnFilter',
    filterValueGetter: ({ data }) => data && (data[id]?.value ?? 'NA'),
    valueGetter: ({ data }) => (data && data[id]?.value) ?? null,
    cellRenderer: ParticipationPercentage,
    sortable: true,
  }),
};
